/**
 * Tipos de dado dos plugins da mesa, sem depender do runtime: o que vira ficha da
 * biblioteca, o que vira tabela da sala e quantas fichas usam cada plugin.
 */
import type { LibraryItem, RoomPlugin } from '../shared.js';

type DataType = RoomPlugin['dataTypes'][number];

/** Ficha da biblioteca (sheetTemplate; sem formType também conta como ficha). */
export const isSheetType = (d: DataType) => !d.formType || d.formType === 'sheetTemplate';
/** Tabela da sala (TablesDock). */
export const isDockType = (d: DataType) => d.formType === 'tablesDock';

/** Um sistema que dá para escolher em "Nova ficha…". */
export type SheetSystem = { moduleId: string; dataType: string; title: string; plugin: string };

/** Os sistemas de ficha dos plugins da mesa, em ordem alfabética. */
export function sheetSystems(plugins: readonly RoomPlugin[]): SheetSystem[] {
  return plugins
    .flatMap((p) => p.dataTypes.filter(isSheetType).map((d) => ({ moduleId: p.moduleId, dataType: d.id, title: d.title || d.id, plugin: p.name })))
    .sort((a, b) => a.title.localeCompare(b.title));
}

const fold = (text: string) => text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

/** Busca sem diferenciar maiúsculas nem acentos, no nome do sistema ou do plugin. */
export function filterSystems(systems: readonly SheetSystem[], query: string): SheetSystem[] {
  const q = fold(query.trim());
  return q ? systems.filter((s) => fold(s.title).includes(q) || fold(s.plugin).includes(q)) : [...systems];
}

/** Tabelas da sala que o plugin traz. */
export const pluginDockTypes = (plugin: RoomPlugin) => plugin.dataTypes.filter(isDockType);

/**
 * Fichas que ficam órfãs se o plugin sair: as dos tipos dele que nenhum outro plugin
 * da mesa também edita.
 */
export function sheetsUsingPlugin(library: readonly LibraryItem[], plugin: RoomPlugin, plugins: readonly RoomPlugin[] = []): number {
  const elsewhere = new Set(plugins.filter((p) => p.moduleId !== plugin.moduleId).flatMap((p) => p.dataTypes.filter(isSheetType).map((d) => d.id)));
  const types = new Set(plugin.dataTypes.filter(isSheetType).map((d) => d.id).filter((id) => !elsewhere.has(id)));
  return library.filter((i) => i.kind === 'sheet' && i.dataType !== null && types.has(i.dataType)).length;
}
